import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  getDailyBook,
  getReconciliationAccountingLinesQuery,
  saveReconciliationAccountingLinesMutation,
} from 'lib/api/accounting';
import { BankReconciliationSaveLinePayload } from 'lib/api/accounting/types';
import { getOwnBankAccountsQuery } from 'lib/api/bankAccounts';
import PageHeader from 'lib/designSystem/PageHeader';
import { errorHandler } from 'lib/functions/errors';
import { formatDateString, showAlert } from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toEndOfDayISO } from '../utils/fetchWithDateChunking';
import BankReconciliationSummaryCards from './components/cards';
import BankReconciliationSearchPanel from './components/search';
import { createBankReconciliationGlobalSearcherFilters } from './components/search/filters';
import BankReconciliationTable, {
  BankReconciliationEntryReference,
} from './components/table';
import BankReconciliationDialogs from './components/table/components/Dialogs';
import BankReconciliationLoadingState from './components/table/components/LoadingState';
import {
  useBankReconciliationEdits,
  useBankReconciliationFilters,
} from './hooks';
import {
  areLineEditsEqual,
  findEntryFromTransactionalDay,
  formatAmount,
  formatDate,
  mapTransactionalEntryToModalEntry,
  normalizeDateOnly,
  normalizeEditDate,
  normalizeResponse,
  toLineEdit,
  TransactionalDay,
} from './utils';

const DEFAULT_LIMIT = 20;
const EMPTY_EDIT_DATE = '';
const MAX_OBSERVATION_LENGTH = 1000;

const BankReconciliation: React.FC = () => {
  const dispatch = useDispatch();
  const { mutateAsync: saveBatch, isPending: isSaving } =
    saveReconciliationAccountingLinesMutation(dispatch);

  const defaultPeriod = useMemo(() => {
    const now = dayjs();
    return {
      start: now.startOf('month').format('YYYY-MM-DD'),
      end: now.endOf('month').format('YYYY-MM-DD'),
    };
  }, []);

  const {
    hasSearched,
    queryFilters,
    queryParams,
    setQueryFilters,
    searchStartDate,
    setSearchStartDate,
    searchEndDate,
    setSearchEndDate,
    searchSelectedBankId,
    setSearchSelectedBankId,
    searchReconciliationFilter,
    setSearchReconciliationFilter,
    searchBusinessIdentifier,
    setSearchBusinessIdentifier,
    searchDescription,
    setSearchDescription,
    searchDebit,
    setSearchDebit,
    searchCredit,
    setSearchCredit,
    searchObservation,
    setSearchObservation,
    searchConciliationDate,
    setSearchConciliationDate,
    handleMinimumSearch,
    handleAdvancedSearch,
  } = useBankReconciliationFilters({
    dispatch,
    defaultPeriod,
    defaultLimit: DEFAULT_LIMIT,
    normalizeDateOnly,
  });

  const [observationDialogRowKey, setObservationDialogRowKey] = useState<
    string | null
  >(null);
  const [observationDraft, setObservationDraft] = useState('');
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entryDialogLoading, setEntryDialogLoading] = useState(false);
  const [entryDialogEntry, setEntryDialogEntry] =
    useState<
      React.ComponentProps<typeof BankReconciliationDialogs>['entryDialogEntry']
    >(null);

  const isBankSelectedForQuery = queryFilters.selectedBankId !== null;
  const isQueryEnabled = hasSearched && isBankSelectedForQuery;
  const { data, isLoading, isFetching, isError, error } =
    getReconciliationAccountingLinesQuery(
      dispatch,
      queryParams,
      isQueryEnabled,
    );
  const {
    data: ownBankAccounts = [],
    isError: isOwnBankAccountsError,
    error: ownBankAccountsError,
  } = getOwnBankAccountsQuery(dispatch, true);

  useEffect(() => {
    if (isError) {
      errorHandler(
        dispatch,
        error,
        'No fue posible cargar las líneas de conciliación bancaria.',
      );
    }
  }, [dispatch, error, isError]);

  useEffect(() => {
    if (isOwnBankAccountsError) {
      errorHandler(
        dispatch,
        ownBankAccountsError,
        'No fue posible cargar las opciones de bancos.',
      );
    }
  }, [dispatch, isOwnBankAccountsError, ownBankAccountsError]);

  const normalizedData = useMemo(
    () => normalizeResponse(data, queryFilters.page, queryFilters.limit),
    [data, queryFilters.limit, queryFilters.page],
  );

  const bankOptions = useMemo(() => {
    const optionsByBankId = new Map<string, { value: string; label: string }>();

    ownBankAccounts.forEach((bankAccount) => {
      const rawBankId = bankAccount.bankId ?? bankAccount.id;
      const parsedBankId = Number(rawBankId);
      if (Number.isNaN(parsedBankId)) return;

      const value = String(parsedBankId);
      if (optionsByBankId.has(value)) return;

      const label = bankAccount.accountNumber
        ? `${bankAccount.bankName} (${bankAccount.accountNumber})`
        : bankAccount.bankName;

      optionsByBankId.set(value, { value, label });
    });

    return Array.from(optionsByBankId.values());
  }, [ownBankAccounts]);

  const selectedBankLabel = useMemo(() => {
    if (queryFilters.selectedBankId === null) return 'Sin banco seleccionado';
    const selectedOption = bankOptions.find(
      (option) => Number(option.value) === queryFilters.selectedBankId,
    );
    return selectedOption?.label ?? `Banco ${queryFilters.selectedBankId}`;
  }, [bankOptions, queryFilters.selectedBankId]);

  const globalSearcherFilters = useMemo(
    () =>
      createBankReconciliationGlobalSearcherFilters({
        searchReconciliationFilter,
        setSearchReconciliationFilter,
        searchBusinessIdentifier,
        setSearchBusinessIdentifier,
        searchDescription,
        setSearchDescription,
        searchDebit,
        setSearchDebit,
        searchCredit,
        setSearchCredit,
        searchObservation,
        setSearchObservation,
        searchConciliationDate,
        setSearchConciliationDate,
      }),
    [
      searchBusinessIdentifier,
      searchConciliationDate,
      searchCredit,
      searchDebit,
      searchDescription,
      searchObservation,
      searchReconciliationFilter,
    ],
  );

  const {
    tableRows,
    allTableRows,
    dirtyRows,
    updateRowEdit,
    setInitialRowEdits,
  } = useBankReconciliationEdits({
    items: normalizedData.items,
    reconciliationFilter: queryFilters.reconciliationFilter,
    toLineEdit: (line, index) =>
      toLineEdit(line, index, (value) =>
        normalizeEditDate(value, EMPTY_EDIT_DATE),
      ),
    areLineEditsEqual,
  });

  const showLoadingSkeleton = hasSearched && (isLoading || isFetching);
  const hasResultRows = hasSearched && allTableRows.length > 0;
  const showResults = hasResultRows && !showLoadingSkeleton;

  const balancesViewModel = useMemo(() => {
    const balances = data?.balances;
    return {
      initial: typeof balances?.initial === 'number' ? balances.initial : 0,
      period: typeof balances?.period === 'number' ? balances.period : 0,
      total: typeof balances?.total === 'number' ? balances.total : 0,
    };
  }, [data]);
  const reconciliationPercentages = useMemo(() => {
    const totalRows = allTableRows.length;
    if (totalRows <= 0) {
      return {
        reconciled: 0,
        unreconciled: 0,
      };
    }

    const reconciledRows = allTableRows.filter(
      (row) => row.edit.isReconciled,
    ).length;
    const reconciled = Math.round((reconciledRows / totalRows) * 100);

    return {
      reconciled,
      unreconciled: 100 - reconciled,
    };
  }, [allTableRows]);
  const reconciliationAmounts = useMemo(() => {
    const getRowAmount = (row: (typeof allTableRows)[number]) => {
      const debit = Math.abs(row.line.debit ?? 0);
      const credit = Math.abs(row.line.credit ?? 0);
      return Math.max(debit, credit);
    };

    return allTableRows.reduce(
      (accumulator, row) => {
        const amount = getRowAmount(row);
        if (row.edit.isReconciled) {
          accumulator.reconciled += amount;
          return accumulator;
        }

        accumulator.unreconciled += amount;
        return accumulator;
      },
      {
        reconciled: 0,
        unreconciled: 0,
      },
    );
  }, [allTableRows]);

  const handleOpenObservationDialog = (rowKey: string, observation: string) => {
    setObservationDialogRowKey(rowKey);
    setObservationDraft(observation);
  };

  const handleCloseObservationDialog = () => {
    setObservationDialogRowKey(null);
    setObservationDraft('');
  };

  const handleSaveObservationDialog = () => {
    if (!observationDialogRowKey) return;
    updateRowEdit(observationDialogRowKey, {
      observation: observationDraft,
    });
    handleCloseObservationDialog();
  };

  const handleOpenEntry = async (
    reference: BankReconciliationEntryReference,
  ) => {
    if (!reference.date || !dayjs(reference.date).isValid()) {
      showAlert(
        dispatch,
        'warning',
        'La línea seleccionada no tiene una fecha válida para consultar el libro transaccional.',
      );
      return;
    }

    try {
      setEntryDialogLoading(true);

      const normalizedDate = normalizeDateOnly(reference.date);
      const params = new URLSearchParams();
      params.append('startDate', normalizedDate);
      params.append('endDate', toEndOfDayISO(normalizedDate));
      params.append('transactional', 'true');

      const transactionalResponse = await getDailyBook(
        dispatch,
        params.toString(),
      );
      const dayEntries = (transactionalResponse?.groupedLinesToDateList ??
        []) as TransactionalDay[];

      const targetDay = dayEntries.find(
        (dayEntry) =>
          typeof dayEntry.date === 'string' &&
          normalizeDateOnly(dayEntry.date) === normalizedDate,
      );

      const groupedEntries = targetDay?.groupedLinesByCategoryAndBusiness ?? [];

      if (groupedEntries.length === 0) {
        showAlert(
          dispatch,
          'warning',
          'No se encontraron asientos en el libro transaccional para la fecha seleccionada.',
        );
        return;
      }

      const selectedEntry = findEntryFromTransactionalDay(
        groupedEntries,
        reference,
      );

      if (!selectedEntry) {
        showAlert(
          dispatch,
          'warning',
          'No fue posible identificar el asiento de esta línea en el libro transaccional.',
        );
        return;
      }

      setEntryDialogEntry(
        mapTransactionalEntryToModalEntry(selectedEntry, normalizedDate),
      );
      setEntryDialogOpen(true);
    } catch (entryError) {
      errorHandler(
        dispatch,
        entryError,
        'No fue posible cargar el asiento desde el libro transaccional.',
      );
    } finally {
      setEntryDialogLoading(false);
    }
  };

  const handleSaveBatch = async () => {
    if (dirtyRows.length === 0) {
      showAlert(dispatch, 'warning', 'No hay cambios para guardar.');
      return;
    }

    const invalidRow = dirtyRows.find(
      (row) => row.edit.isReconciled && !row.edit.reconciledAt,
    );

    if (invalidRow) {
      showAlert(
        dispatch,
        'warning',
        'Cada línea conciliada debe tener fecha de conciliación.',
      );
      return;
    }

    const missingLineKeyRow = dirtyRows.find(
      (row) => !row.edit.lineKey || row.edit.lineKey.trim().length === 0,
    );

    if (missingLineKeyRow) {
      showAlert(
        dispatch,
        'warning',
        'Hay líneas modificadas sin lineKey. No se puede guardar la conciliación.',
      );
      return;
    }

    const payloadLines: BankReconciliationSaveLinePayload[] = dirtyRows.map(
      ({ edit }) => ({
        lineKey: edit.lineKey as string,
        isConciliated: edit.isReconciled,
        conciliationDate: edit.isReconciled
          ? dayjs(edit.reconciledAt).startOf('day').toISOString()
          : null,
        observation: edit.observation.trim() || null,
      }),
    );

    try {
      await saveBatch({
        lines: payloadLines,
      });

      showAlert(dispatch, 'success', 'Conciliación guardada correctamente.');
      setInitialRowEdits((previous) => {
        const nextInitial = { ...previous };
        dirtyRows.forEach(({ rowKey, edit }) => {
          nextInitial[rowKey] = edit;
        });
        return nextInitial;
      });
    } catch (saveError) {
      errorHandler(
        dispatch,
        saveError,
        'No fue posible guardar la conciliación bancaria.',
      );
    }
  };

  const periodLabel = `Período: ${formatDateString(queryFilters.startDate, 'dd/mm/yyyy')} - ${formatDateString(queryFilters.endDate, 'dd/mm/yyyy')}`;

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader.WithTitleAndActions
        title="Conciliación Bancaria"
        subtitle="Ingresa fecha de inicio, término y banco."
        iconType="calendar"
      />

      <BankReconciliationSearchPanel
        searchStartDate={searchStartDate}
        searchEndDate={searchEndDate}
        searchSelectedBankId={searchSelectedBankId}
        bankOptions={bankOptions}
        globalSearcherFilters={globalSearcherFilters}
        periodLabel={periodLabel}
        selectedBankLabel={selectedBankLabel}
        isSaving={isSaving}
        hasDirtyRows={dirtyRows.length > 0}
        onStartDateChange={setSearchStartDate}
        onEndDateChange={setSearchEndDate}
        onSelectedBankChange={setSearchSelectedBankId}
        onMinimumSearch={handleMinimumSearch}
        onAdvancedSearch={handleAdvancedSearch}
        onSave={() => void handleSaveBatch()}
      />

      {showLoadingSkeleton && <BankReconciliationLoadingState />}

      {showResults && (
        <>
          <BankReconciliationSummaryCards
            reconciliationPercentages={reconciliationPercentages}
            reconciliationAmounts={reconciliationAmounts}
            balances={balancesViewModel}
            formatAmount={formatAmount}
          />

          <BankReconciliationTable
            rows={tableRows}
            totalItems={normalizedData.totalItems}
            page={Math.max(normalizedData.currentPage - 1, 0)}
            rowsPerPage={normalizedData.pageSize}
            emptyEditDate={EMPTY_EDIT_DATE}
            formatDate={formatDate}
            formatAmount={formatAmount}
            onToggleReconciled={(rowKey, isChecked, reconciledAt) =>
              updateRowEdit(rowKey, {
                isReconciled: isChecked,
                reconciledAt: isChecked ? reconciledAt : null,
              })
            }
            onOpenObservationDialog={handleOpenObservationDialog}
            onOpenEntry={(reference) => {
              void handleOpenEntry(reference);
            }}
            onReconciledAtChange={(rowKey, reconciledAt) =>
              updateRowEdit(rowKey, { reconciledAt })
            }
            onPageChange={(newPage) =>
              setQueryFilters((previous) => ({
                ...previous,
                page: newPage + 1,
              }))
            }
            onRowsPerPageChange={(nextLimit) =>
              setQueryFilters((previous) => ({
                ...previous,
                limit: Number.isNaN(nextLimit) ? DEFAULT_LIMIT : nextLimit,
                page: 1,
              }))
            }
          />
        </>
      )}

      <BankReconciliationDialogs
        entryDialogLoading={entryDialogLoading}
        entryDialogOpen={entryDialogOpen}
        entryDialogEntry={entryDialogEntry}
        observationDialogOpen={Boolean(observationDialogRowKey)}
        observationDraft={observationDraft}
        maxObservationLength={MAX_OBSERVATION_LENGTH}
        onCloseEntryDialog={() => setEntryDialogOpen(false)}
        onCloseObservationDialog={handleCloseObservationDialog}
        onSaveObservationDialog={handleSaveObservationDialog}
        onObservationDraftChange={setObservationDraft}
      />
    </Box>
  );
};

export default BankReconciliation;
